<template lang="pug">
  .slick-slider.swiper(:ref="'swiper-' + uniqueId" :class="swiperClass")
    .swiper-wrapper
      //- Wrap default slot content in swiper-slide divs
      component(v-for="slide in renderWithClass()" :key="slide.key" :is="slide")
    
    //- Navigation arrows
    .swiper-button-prev(:ref="'prev-' + uniqueId" :key="breakpoint")

    .swiper-button-next(:ref="'next-' + uniqueId" :key="breakpoint")  
    
    //- Pagination
    .swiper-pagination(:ref="'pagination-' + uniqueId" :key="breakpoint")
</template>

<script>
  import { Swiper } from 'swiper';
  import { useId } from 'vue';
  import { Navigation, Pagination, Autoplay, EffectFade, Controller } from 'swiper/modules';
  import { mapGetters } from 'vuex';
  import { extractSlides } from './utils';
  import { h } from 'vue';

  // Import Swiper styles
  import 'swiper/css';
  import 'swiper/css/navigation';
  import 'swiper/css/pagination';
  import 'swiper/css/effect-fade';

  export default {
    name: 'SlickCarouselWrapper',

    props: {
      // All vue-slick-carousel props - we accept them and transform to Swiper format
      accessibility: {
        type: Boolean,
        default: true,
      },
      adaptiveHeight: {
        type: Boolean,
        default: false,
      },
      arrows: {
        type: Boolean,
        default: true,
      },
      asNavFor: {
        type: Object,
        default: null,
      },
      autoplay: {
        type: Boolean,
        default: false,
      },
      autoplaySpeed: {
        type: Number,
        default: 3000,
      },
      centerMode: {
        type: Boolean,
        default: false,
      },
      centerPadding: {
        type: String,
        default: '50px',
      },
      cssEase: {
        type: String,
        default: 'ease',
      },
      dots: {
        type: Boolean,
        default: false,
      },
      dotsClass: {
        type: String,
        default: 'slick-dots',
      },
      draggable: {
        type: Boolean,
        default: true,
      },
      edgeFriction: {
        type: Number,
        default: 0.35,
      },
      fade: {
        type: Boolean,
        default: false,
      },
      focusOnSelect: {
        type: Boolean,
        default: false,
      },
      infinite: {
        type: Boolean,
        default: true,
      },
      initialSlide: {
        type: Number,
        default: 0,
      },
      lazyLoad: {
        type: [String, Boolean],
        default: false,
        validator: value => [false, 'ondemand', 'progressive'].includes(value),
      },
      pauseOnDotsHover: {
        type: Boolean,
        default: false,
      },
      pauseOnFocus: {
        type: Boolean,
        default: false,
      },
      pauseOnHover: {
        type: Boolean,
        default: true,
      },
      responsive: {
        type: Array,
        default: () => [],
      },
      rows: {
        type: Number,
        default: 1,
      },
      rtl: {
        type: Boolean,
        default: false,
      },
      slidesPerRow: {
        type: Number,
        default: 1,
      },
      slidesToScroll: {
        type: Number,
        default: 1,
      },
      slidesToShow: {
        type: Number,
        default: 1,
      },
      speed: {
        type: Number,
        default: 500,
      },
      swipe: {
        type: Boolean,
        default: true,
      },
      swipeToSlide: {
        type: Boolean,
        default: false,
      },
      touchMove: {
        type: Boolean,
        default: true,
      },
      touchThreshold: {
        type: Number,
        default: 5,
      },
      useCSS: {
        type: Boolean,
        default: true,
      },
      useTransform: {
        type: Boolean,
        default: true,
      },
      variableWidth: {
        type: Boolean,
        default: false,
      },
      vertical: {
        type: Boolean,
        default: false,
      },
      waitForAnimate: {
        type: Boolean,
        default: true,
      },
      mobileFirst: {
        type: Boolean,
        default: false,
      },
    },

    emits: ['init', 'beforeChange', 'afterChange', 'swipe'],

    setup() {
      const uniqueId = useId();
      return { uniqueId };
    },

    data() {
      return {
        swiper: null,
      };
    },

    computed: {
      ...mapGetters('global', [
        'breakpoint'
      ]),

      slides() {
        return extractSlides(this.$slots.default());
      },

      slick() {
        // Return the swiper instance to maintain compatibility with existing code
        return this.swiper;
      },

      swiperClass() {
        return `swiper-${this.uniqueId}`;
      },

      showArrows() {
        return this.arrows;
      },

      showDots() {
        return this.dots;
      },

      swiperConfig() {
        let baseConfig = {
          slidesToShow: this.slidesToShow,
          slidesToScroll: this.slidesToScroll,
          infinite: this.infinite,
          arrows: this.arrows,
          dots: this.dots,
          autoplay: this.autoplay,
          autoplaySpeed: this.autoplaySpeed,
          pauseOnHover: this.pauseOnHover,
          fade: this.fade,
          swipe: this.swipe,
          speed: this.speed,
          vertical: this.vertical,
          centerMode: this.centerMode,
          initialSlide: this.initialSlide,
        };

        // If there's responsive config, use defaultSettings as base
        let convertedBreakpoints;
        if (this.responsive && this.responsive.length > 0) {
          const { result, defaultSettings } = this.convertBreakpointsToMobileFirst(this.responsive);
          convertedBreakpoints = result;
          if (defaultSettings) {
            baseConfig = { ...baseConfig, ...defaultSettings };
          }
        }

        const config = this.mapSlickConfigToSwiper(baseConfig);
        config.modules = [Navigation, Pagination, Autoplay, EffectFade, Controller];
        config.slideActiveClass = 'slick-active';

        // Responsive breakpoints - ADAPTATION FROM DESKTOP FIRST TO MOBILE FIRST
        if (this.responsive && this.responsive.length > 0) {
          config.breakpoints = {};

          convertedBreakpoints.forEach(breakpoint => {
            const breakpointConfig = this.mapSlickConfigToSwiper(breakpoint.settings);
            config.breakpoints[breakpoint.breakpoint] = breakpointConfig;
          });
        }

        config.observer = true;

        // For vertical carousels, disable auto height calculation
        if (this.vertical) {
          config.autoHeight = false;
          config.freeMode = false;
        }

        return config;
      },

    },

    watch: {
      // Watch for changes in key props that require reinitialization
      slidesToShow() {
        this.reinitializeSwiper();
      },
      responsive: {
        handler() {
          this.reinitializeSwiper();
        },
        deep: true,
      },
      breakpoint() {
        this.reinitializeSwiper();
      },
    },

    mounted() {
      this.initSwiper();
    },

    beforeUnmount() {
      if (this.swiper) {
        this.swiper.destroy();
        this.swiper = null;
      }
    },

    methods: {
      renderWithClass() {
        return this.slides.map(slide => {
          return h('div', {
            class: 'swiper-slide',
          }, [slide]);
        });
      },

      // METHOD TO MAP SLICK CONFIGURATION TO SWIPER
      mapSlickConfigToSwiper(slickConfig) {
        const swiperConfig = {
          slidesPerView: slickConfig.slidesToShow || 1,
          spaceBetween: slickConfig.spaceBetween || 0,
          speed: slickConfig.speed || 500,
          loop: slickConfig.infinite !== undefined ? slickConfig.infinite : false,
          direction: slickConfig.vertical ? 'vertical' : 'horizontal',
          autoHeight: slickConfig.adaptiveHeight || false,
          centeredSlides: slickConfig.centerMode || false,
          initialSlide: slickConfig.initialSlide || 0,
          allowTouchMove: slickConfig.swipe !== false && slickConfig.draggable !== false,
          touchReleaseOnEdges: !slickConfig.infinite,
          slidesPerGroup: slickConfig.slidesToScroll || 1,
          slideToClickedSlide: true,
        };

        // Handle fade effect
        if (slickConfig.fade && slickConfig.slidesToShow === 1) {
          swiperConfig.effect = 'fade';
          swiperConfig.fadeEffect = { crossFade: true };
        }

        // Handle navigation/arrows
        if (slickConfig.arrows) {
          swiperConfig.navigation = {
            nextEl: `.${this.swiperClass} .swiper-button-next`,
            prevEl: `.${this.swiperClass} .swiper-button-prev`,
          };
        } else if (slickConfig.arrows === false) {
          swiperConfig.navigation = false;
        }

        // Handle pagination/dots
        if (slickConfig.dots) {
          swiperConfig.pagination = {
            el: `.${this.swiperClass} .swiper-pagination`,
            clickable: true,
          };
        } else if (!slickConfig.dots) {
          swiperConfig.pagination = false;
        }

        // Handle autoplay
        if (slickConfig.autoplay) {
          swiperConfig.autoplay = {
            delay: slickConfig.autoplaySpeed || 3000,
            pauseOnMouseEnter: slickConfig.pauseOnHover !== false,
            disableOnInteraction: false,
          };
        } else if (slickConfig.autoplay === false) {
          swiperConfig.autoplay = false;
        }

        return swiperConfig;
      },

      // METHOD TO CONVERT BREAKPOINTS FROM DESKTOP-FIRST TO MOBILE-FIRST
      convertBreakpointsToMobileFirst(responsiveConfig) {
        if (!responsiveConfig || responsiveConfig.length === 0) {
          return [];
        }

        // Sort breakpoints from smallest to largest
        const sortedBreakpoints = [...responsiveConfig].sort((a, b) => a.breakpoint - b.breakpoint);

        const result = [];

        // Start from the second breakpoint (first one is already the base config)
        for (let i = 1; i < sortedBreakpoints.length; i++) {
          const currentBreakpoint = sortedBreakpoints[i];

          // The breakpoint is just after the previous breakpoint
          const mobileFirstBreakpoint = sortedBreakpoints[i - 1].breakpoint + 1;

          if (!currentBreakpoint.settings.slidesToShow) {
            currentBreakpoint.settings.slidesToShow = this.slidesToShow;
          }

          result.push({
            breakpoint: mobileFirstBreakpoint,
            settings: currentBreakpoint.settings
          });
        }

        // Add desktop breakpoint (after the last breakpoint)
        if (sortedBreakpoints.length > 0) {
          const lastBreakpoint = sortedBreakpoints[sortedBreakpoints.length - 1];
          result.push({
            breakpoint: lastBreakpoint.breakpoint + 1,
            settings: {
              slidesToShow: this.slidesToShow,
              slidesToScroll: this.slidesToScroll,
              infinite: this.infinite,
              arrows: this.arrows,
              dots: this.dots,
              autoplay: this.autoplay,
              autoplaySpeed: this.autoplaySpeed,
              pauseOnHover: this.pauseOnHover,
              fade: this.fade,
              swipe: this.swipe
            }
          });
        }

        return { result, defaultSettings: sortedBreakpoints[0]?.settings };
      },

      initSwiper() {
        this.$nextTick(() => {
          const swiperEl = this.$refs[`swiper-${this.uniqueId}`];
          if (!swiperEl) {
            return;
          }

          // Initialize Swiper
          const self = this;
          this.swiper = new Swiper(swiperEl, {
            ...this.swiperConfig,
            on: {
              init: (swiper) => {
                self.emitEvent('init', swiper);
              },
              slideChangeTransitionStart: (swiper) => {
                self.emitEvent('beforeChange', swiper.previousIndex, swiper.activeIndex);
              },
              slideChangeTransitionEnd: (swiper) => {
                self.emitEvent('afterChange', swiper.previousIndex, swiper.activeIndex);
              },
              click: () => {
                self.$nextTick(() => {
                  setTimeout(() => {
                    if (self.asNavFor) {
                      const asNavFor = self.asNavFor?.slick ?? self.asNavFor;
                      if (self.infinite) {
                        asNavFor.slideToLoop(self.swiper.realIndex);
                      } else {
                        asNavFor.slideTo(self.swiper.realIndex);
                      }
                    }
                  }, 200);
                });
              },

              destroy: () => {
                self.swiper = null;
              },
            },
          });
        });
      },

      emitEvent(eventName, ...args) {
        // Map Swiper events to Slick event names and emit them
        const eventMap = {
          init: 'init',
          beforeChange: 'beforeChange',
          afterChange: 'afterChange',
          swipe: 'swipe',
        };

        const slickEventName = eventMap[eventName] || eventName;
        this.$emit(slickEventName, ...args);

        // Also check for event handlers in attrs (for compatibility)
        const handlerName = `on${slickEventName.charAt(0).toUpperCase() + slickEventName.slice(1)}`;
        if (this.$attrs[handlerName]) {
          this.$attrs[handlerName](...args);
        }
      },

      // Expose Slick-compatible methods
      prev() {
        if (this.swiper) {
          this.swiper.slidePrev();

          const asNavFor = this.asNavFor?.slick ?? this.asNavFor;
          if (this.asNavFor) {
            if (this.infinite) {
              asNavFor.slideToLoop(this.swiper.realIndex);
            } else {
              asNavFor.slideTo(this.swiper.realIndex);
            }
          }
        }
      },

      next() {
        if (this.swiper) {
          this.swiper.slideNext();

          const asNavFor = this.asNavFor?.slick ?? this.asNavFor;
          if (this.asNavFor) {
            if (this.infinite) {
              asNavFor.slideToLoop(this.swiper.realIndex);
            } else {
              asNavFor.slideTo(this.swiper.realIndex);
            }
          }
        }
      },

      goTo(slide, dontAnimate = false) {
        if (this.swiper) {
          const speed = dontAnimate ? 0 : this.speed;
          this.swiper.slideTo(slide, speed);
        }
      },

      play() {
        if (this.swiper && this.swiper.autoplay) {
          this.swiper.autoplay.start();
        }
      },

      pause() {
        if (this.swiper && this.swiper.autoplay) {
          this.swiper.autoplay.stop();
        }
      },

      update() {
        if (this.swiper) {
          this.swiper.update();
        }
      },

      // Swiper-specific methods for compatibility
      destroy() {
        if (this.swiper) {
          this.swiper.destroy();
          this.swiper = null;
        }
      },

      // Helper method to get current slide
      getCurrentSlide() {
        return this.swiper ? this.swiper.activeIndex : 0;
      },

      // Reinitialize Swiper when props change
      reinitializeSwiper() {
        if (this.swiper) {
          this.destroy();
          this.$nextTick(() => {
            this.initSwiper();
          });
        }
      },
    },
  };
</script>

<style lang="stylus">
  .swiper-pagination
    display block!important
    position absolute!important
    margin-top -1em!important
  
  .swiper-pagination-bullet
      background #fff
      opacity: 0.5

  .swiper-pagination-bullet-active
    opacity: 1

  .swiper
    // Ensure swiper inherits width properly
    width: 100%
    
    .swiper-slide
      // Maintain existing slick slide behavior
      box-sizing: border-box
      
    // Maintain compatibility with existing slick styling
    &.slick-slider
      margin-bottom: 0
      
    .swiper-button-prev,
    .swiper-button-next
      // Style navigation buttons to match slick arrows if needed
      color: inherit
      
    .swiper-pagination
      // Style pagination to match slick dots if needed
      position: relative
      margin-top: 1em
      
    // Vertical carousel support
    &.swiper-vertical
      height: 100%
      
      .swiper-wrapper
        flex-direction: column
        height: 100% !important
        
      .swiper-slide
        width: auto !important
        flex-shrink: 0
        box-sizing: border-box
        
    // Hide default swiper buttons when they're not being used
    .swiper-button-prev,
    .swiper-button-next
      display: none
      
    .swiper-pagination
      display: none
</style>